namespace $ {
	// Allocation state is scoped to the import mapping, not to a render or action
	// attempt. Even suspension before Data()/meta() must reuse the same area.
	const $shm_hitalama_transfer_map_widget_drafts = new WeakMap< Map< string, any >,
		Map< string, { idea: number } > >
	const $shm_hitalama_transfer_map_deserialize_states = new WeakMap< object, Map< string, {
		done: boolean
		pawns: Map< string, any >
		nodes: Map< string, $shm_hitalama_transfer_node >
		blobs: Map< string, Blob >
	} > >

	export type $shm_hitalama_transfer_map_dump = $shm_hitalama_transfer_dump
	export type $shm_hitalama_transfer_map_widgets_dump = {
		format: 'hitalama_transfer_map_widgets'
		root_links: string[]
		removed_widget_links?: string[]
		nodes: $shm_hitalama_transfer_node[]
	}

	export class $shm_hitalama_transfer_map extends $shm_hitalama_transfer {

		map(): $shm_hitalama_map {
			throw new Error('Not implemented')
		}

		entity(): $giper_baza_dict {
			return this.map()
		}

		include_files() {
			return true
		}

		protected should_serialize_field(
			owner: $giper_baza_dict,
			field: string,
			field_pawn: any,
		) {
			if( owner instanceof $shm_hitalama_map_widget && field === 'Inside_widget' ) return false
			if( !this.include_files() ) {
				if( field_pawn instanceof $giper_baza_file ) return false
				if( field_pawn instanceof $giper_baza_atom_link && ( field_pawn as any ).remote?.() instanceof $giper_baza_file ) return false
			}

			if( owner === this.map() ) {
				if( field === 'Widget_by_name' ) return false
				if( field === 'Presences' ) return false
			}

			return super.should_serialize_field( owner, field, field_pawn )
		}

		protected should_serialize_node( context: $shm_hitalama_transfer_node_context ) {
			if( context.node === this.map() && context.owner !== this.map() ) return false
			if( !this.include_files() && context.node instanceof $giper_baza_file ) return false
			return super.should_serialize_node( context )
		}

		protected follow_serialize_node(
			owner: $giper_baza_dict,
			field: string,
			kind: Extract< $shm_hitalama_transfer_field_kind, 'link' | 'links' | 'owned' >,
			target: $giper_baza_dict | null,
			visited: Map< string, $shm_hitalama_transfer_node >,
		) {
			if( owner instanceof $shm_hitalama_map_widget && field === 'Widgets' && target ) {
				target = target.cast( $shm_hitalama_map_widget )
			}

			return super.follow_serialize_node( owner, field, kind, target, visited )
		}

		serialize_map(): $shm_hitalama_transfer_map_dump {
			return this.serialize_table2_values( super.serialize() )
		}

		protected serialize_table2_values< Dump extends { nodes: $shm_hitalama_transfer_node[] } >( dto: Dump ): Dump {
			const nodes = new Map( dto.nodes.map( node => [ node.link, node ] ) )
			for( const dataset_node of dto.nodes ) {
				if( dataset_node.class_name !== '$shm_hitalama_map_widget_entity_table2_dataset' ) continue
				const dataset = $giper_baza_glob.Pawn(
					new $giper_baza_link( dataset_node.link ), $shm_hitalama_map_widget_entity_table2_dataset,
				)
				const fields = dataset.fields()
				for( const record of dataset.records_all() ) {
					const node = nodes.get( record.link().toString() )
					if( !node ) continue
					node.table2_values = fields.flatMap( field => {
						if( field.type() === 'file' && !this.include_files() ) return []
						const value = record.value( field )
						if( field.type() === 'file' ) {
							const file = typeof value === 'string' && value
								? $giper_baza_glob.Pawn( new $giper_baza_link( value ), $shm_hitalama_file ) : null
							if( file instanceof $shm_hitalama_file ) this.serialize_node( file, nodes )
						}
						return [[ field.link().toString(), value ] as [ string, any ]]
					} )
				}
			}
			dto.nodes = [ ...nodes.values() ]
			return dto
		}

		protected widget_descendants( widget: $shm_hitalama_map_widget ): readonly $shm_hitalama_map_widget[] {
			return widget.widgets().flatMap( child => [ child, ... this.widget_descendants( child ) ] )
		}

		protected widget_descendants_before_parent( widget: $shm_hitalama_map_widget ): readonly $shm_hitalama_map_widget[] {
			return widget.widgets().flatMap( child => [ ... this.widget_descendants_before_parent( child ), child ] )
		}

		protected widget_roots( source: readonly $shm_hitalama_map_widget[] ) {
			const selected = new Set( source.map( widget => widget.link().toString() ) )
			const inner = new Set(
				source.flatMap( widget => this.widget_descendants( widget ).map( child => child.link().toString() ) )
			)

			return source.filter( widget => !inner.has( widget.link().toString() ) || !selected.has( widget.link().toString() ) )
		}

		serialize_widgets( source: readonly $shm_hitalama_map_widget[] ): $shm_hitalama_transfer_map_widgets_dump {
			const visited = new Map< string, $shm_hitalama_transfer_node >()
			const root_links: string[] = []
			const roots = this.widget_roots( source )

			for( const widget of roots ) {
				root_links.push( widget.link().toString() )
				for( const child of [ ... this.widget_descendants_before_parent( widget ), widget ] ) {
					this.serialize_node( child, visited )
				}
			}

			const nodes = [ ...visited.values() ]
			const removed_widget_links = nodes.flatMap( node => {
				if( node.class_name !== '$shm_hitalama_map_widget' ) return []
				const widget = this.map().widget_by_link( new $giper_baza_link( node.link ) )
				return widget.removed() ? [ node.link ] : []
			} )

			return this.serialize_table2_values({
				format: 'hitalama_transfer_map_widgets',
				root_links,
				removed_widget_links,
				nodes,
			})
		}

		protected instantiate_table2_files( dto: $shm_hitalama_transfer_map_dump | $shm_hitalama_transfer_map_widgets_dump ) {
			for( const node of dto.nodes ) {
				for( const [, value ] of node.table2_values ?? [] ) {
					if( typeof value !== 'string' ) continue
					const file_node = this.node_by_old_link.get( value )
					if( file_node?.class_name !== '$shm_hitalama_file' ) continue
					if( this.instantiate_mapped_child( value ) ) continue
					const file = this.map().file_make()
					if( !file ) continue
					this.set_mapping( value, file )
					this.instantiate_children( file, file_node )
				}
			}
		}

		protected restore_table2_values( dto: $shm_hitalama_transfer_map_dump | $shm_hitalama_transfer_map_widgets_dump ) {
			for( const node of dto.nodes ) {
				if( !node.table2_values?.length ) continue
				const record = this.pawn_by_old_link.get( node.link )
				if( !( record instanceof $shm_hitalama_map_widget_entity_table2_record ) ) continue
				for( const [ field_link, original ] of node.table2_values ) {
					const field = this.pawn_by_old_link.get( field_link )
					if( !( field instanceof $shm_hitalama_map_widget_entity_table2_field ) ) continue
					const remap = ( link: string ) => this.pawn_by_old_link.get( link )?.link().toString() ?? link
					const value = field.type() === 'list_link' && Array.isArray( original )
						? original.map( link => remap( String( link ) ) )
						: field.type() === 'atom_link' || field.type() === 'file'
							? original ? remap( String( original ) ) : ''
							: original
					record.value_write_raw( field, value, true )
				}
			}
		}

		protected instantiate_widget_node( node: $shm_hitalama_transfer_node, config: $giper_baza_land ) {
			let drafts = $shm_hitalama_transfer_map_widget_drafts.get( this.pawn_by_old_link )
			if( !drafts ) {
				drafts = new Map
				$shm_hitalama_transfer_map_widget_drafts.set( this.pawn_by_old_link, drafts )
			}
			let draft = drafts.get( node.link )
			if( !draft ) {
				draft = { idea: Math.floor( Math.random() * 2**48 ) }
				drafts.set( node.link, draft )
			}
			const area = config.area_make( draft.idea )
			const widget = area.Pawn( $shm_hitalama_map_widget ).Data()
			if( $shm_hitalama_map_widget.meta ) widget.meta( $shm_hitalama_map_widget.meta )
			widget.Map( null )?.remote( this.map() )
			return widget
		}

		protected publish_widgets() {
			const list = this.map().Widgets_list( null )
			for( const [ link, pawn ] of this.pawn_by_old_link ) {
				if( this.node_by_old_link.get( link )?.class_name !== '$shm_hitalama_map_widget' ) continue
				if( !list?.has( pawn.link() ) ) list?.add( pawn.link() )
			}
		}

		protected instantiate_collection_node(
			node: $shm_hitalama_transfer_node,
			owner: any,
			field: string,
		) {
			const config = this.child_config( owner ) ?? this.map().land()

			if( owner instanceof $shm_hitalama_map_widget && field === 'Widgets' ) {
				return this.instantiate_widget_node( node, config )
			}

			switch( node.class_name ) {
				case '$shm_hitalama_map_widget': {
					return this.instantiate_widget_node( node, config )
				}
				case '$shm_hitalama_map_widget_entity_table': {
					const table = this.instantiate_field_node( this.map(), 'Tables', node.link, config )
					table?.Map(null)?.remote( this.map() )
					return table
				}
				case '$shm_hitalama_file': {
					if( owner === this.map() && field === 'Files' ) return null
					return this.instantiate_field_node( this.map(), 'Files', node.link, config )
				}
				case '$shm_hitalama_map_widget_entity_custom':
					return this.instantiate_field_node( this.map(), 'Customs', node.link, config )
				case '$shm_hitalama_map_widget_entity_form':
					return this.instantiate_field_node( this.map(), 'Search_statistics', node.link, config )
			}

			return null
		}

		@ $mol_action
		protected instantiate_list_child( host: any, node: $shm_hitalama_transfer_node ) {
			return this.instantiate_collection_node( node, host, 'items' )
				?? super.instantiate_list_child( host, node )
		}

		protected instantiate_field_child(
			host: any,
			field: string,
			kind: Extract< $shm_hitalama_transfer_field_kind, 'link' | 'links' | 'owned' >,
			link: string | null,
		) {
			if( !link ) return
			if( this.instantiate_mapped_child( link ) ) return
			if( !this.should_instantiate_child( host, field, kind, link ) ) return

			const child_dto = this.node_by_old_link.get( link )
			if( !child_dto ) return

			if( host instanceof $shm_hitalama_map_widget_entity_table2_dataset
				&& kind === 'links' && ( field === 'Fields' || field === 'Records' ) ) {
				const child = this.instantiate_field_node( host, field, link, null )
				if( !child ) return
				this.set_mapping( link, child )
				this.instantiate_children( child, child_dto )
				return
			}

			const child = this.instantiate_collection_node( child_dto, host, field )
			if( child ) {
				this.set_mapping( link, child )
				this.instantiate_children( child, child_dto )
				return
			}

			super.instantiate_field_child( host, field, kind, link )
		}

		protected instantiate_entry_child(
			host: any,
			key: string,
			kind: Extract< $shm_hitalama_transfer_field_kind, 'link' | 'links' | 'owned' >,
			link: string | null,
		) {
			if( !link ) return
			if( this.instantiate_mapped_child( link ) ) return
			if( !this.should_instantiate_child( host, key, kind, link ) ) return

			const child_dto = this.node_by_old_link.get( link )
			if( !child_dto ) return

			const child = this.instantiate_collection_node( child_dto, host, key )
			if( child ) {
				this.set_mapping( link, child )
				this.instantiate_children( child, child_dto )
				return
			}

			super.instantiate_entry_child( host, key, kind, link )
		}

		protected serialize_field(
			field_pawn: any,
		): $shm_hitalama_transfer_field | undefined {
			if( field_pawn instanceof $giper_baza_atom_link ) {
				const remote = ( field_pawn as any ).remote?.()
				if( remote instanceof $giper_baza_dict && !remote.filled() ) return undefined
			}

			return super.serialize_field( field_pawn )
		}

		protected remap_imported_links( value: any ): any {
			if( typeof value === 'string' ) {
				let next = value
				const old_map_link = [ ... this.pawn_by_old_link ].find( ([, pawn]) => pawn === this.map() )?.[0]
				const new_map_link = this.map().link().toString()
				const links = [ ... this.pawn_by_old_link ].sort(
					( [ left ], [ right ] )=> right.length - left.length
				)
				for( const [ old_link, pawn ] of links ) {
					const new_link = pawn?.link?.().toString?.()
					if( !new_link || old_link === new_link ) continue
					next = next.replaceAll( old_link, new_link )
					if( old_map_link && old_link.startsWith( old_map_link + '_' ) ) {
						const old_suffix_on_new_map = new_map_link + old_link.slice( old_map_link.length )
						if( old_suffix_on_new_map !== new_link ) next = next.replaceAll( old_suffix_on_new_map, new_link )
					}
					const old_valid_link = $giper_baza_link.check( old_link )
					if( !old_valid_link ) continue
					const old_head = old_valid_link.head().toString()
					if( old_head ) {
						const old_head_on_new_map = new $giper_baza_link( '___' + old_head ).resolve( this.map().link() ).toString()
						if( old_head_on_new_map !== new_link ) next = next.replaceAll( old_head_on_new_map, new_link )
					}
				}
				return next
			}

			if( Array.isArray( value ) ) {
				return value.map( item => this.remap_imported_links( item ) )
			}

			if( value && typeof value === 'object' && Object.getPrototypeOf( value ) === Object.prototype ) {
				return Object.fromEntries(
					Object.entries( value ).map( ([ key, val ]) => [
						key,
						this.remap_imported_links( val ),
					] )
				)
			}

			return value
		}

		protected normalize_field_value( field: string, value: any ) {
			return this.remap_imported_links( super.normalize_field_value( field, value ) )
		}

		protected atom_value( field: $shm_hitalama_transfer_field | undefined ) {
			return field?.kind === 'atom' ? field.value : undefined
		}

		protected text_value( field: $shm_hitalama_transfer_field | undefined ) {
			if( field?.kind === 'atom' || field?.kind === 'text' ) return field.value
			return undefined
		}

		protected normalize_widget_render_data_node( node: $shm_hitalama_transfer_node ) {
			if( node.class_name !== '$shm_hitalama_map_widget' ) return

			const fields = node.fields
			const render = this.atom_value( fields.Render_data )
			const title = render?.title ?? this.atom_value( fields.Title ) ?? node.title ?? node.link
			const type = render?.type ?? this.atom_value( fields.Type ) ?? 'text'
			const text = render?.text ?? this.text_value( fields.Text_atom ) ?? this.text_value( fields.Text ) ?? ''
			const body_x = render?.body_x ?? this.atom_value( fields.Body_x ) ?? 0
			const body_y = render?.body_y ?? this.atom_value( fields.Body_y ) ?? 0
			const right_edge_x = render?.right_edge_x ?? this.atom_value( fields.Right_edge_x ) ?? 0
			const bottom_edge_y = render?.bottom_edge_y ?? this.atom_value( fields.Bottom_edge_y ) ?? 0
			const opacity = render?.opacity ?? this.atom_value( fields.Opacity ) ?? 1

			fields.Render_data = {
				kind: 'atom',
				value: {
					title,
					type,
					text,
					value_refs: render?.value_refs ?? $shm_hitalama_map_widget_value_refs( text ),
					body_x,
					body_y,
					right_edge_x,
					bottom_edge_y,
					opacity,
				},
			}
		}

		protected normalize_render_data( dto: $shm_hitalama_transfer_dump | $shm_hitalama_transfer_map_widgets_dump ) {
			for( const node of dto.nodes ) this.normalize_widget_render_data_node( node )
			return dto
		}

		protected override deserialize_field_data( host: any, field: string, dto: $shm_hitalama_transfer_field ) {
			if( host instanceof $shm_hitalama_map_widget && field === 'Render_data' && dto.kind === 'atom' ) {
				// set_title() may already have read the lazy render channel. Write through
				// its owner so both the CRDT atom and that channel receive the imported value.
				return $shm_hitalama_map_history.silence( ()=>
					host.render_data( this.normalize_field_value( field, dto.value ) ),
				)
			}
			return super.deserialize_field_data( host, field, dto )
		}

		protected rebuild_widget_index() {
			const dict = this.map().Widget_by_name(null)
			dict?.keys().forEach( key => {
				dict?.key( key )?.remote( null as any )
			} )

			this.map().Widgets_list()?.remote_list().forEach( ( widget: $shm_hitalama_map_widget ) => {
				dict?.key( widget.title(), 'auto' ).remote( widget )
			} )
		}

		protected relink_widget_groups( dto: $shm_hitalama_transfer_map_widgets_dump ) {
			for( const node of dto.nodes ) {
				if( node.class_name !== '$shm_hitalama_map_widget' ) continue

				const group = this.pawn_by_old_link.get( node.link )?.cast?.( $shm_hitalama_map_widget )
				if( group?.type?.() !== 'group' ) continue

				const field = node.fields.Widgets
				if( field?.kind !== 'links' ) continue

				const widgets = field.links.flatMap( link => {
					const widget = this.pawn_by_old_link.get( link )?.cast?.( $shm_hitalama_map_widget )
					if( !widget ) return []
					widget.container_widget( group )
					return [ widget ]
				} )

				group.widgets( widgets )
			}
		}

		protected restore_removed_widgets( dto: $shm_hitalama_transfer_map_widgets_dump ) {
			for( const old_link of dto.removed_widget_links ?? [] ) {
				const widget = this.pawn_by_old_link.get( old_link )?.cast?.( $shm_hitalama_map_widget )
				if( !widget ) continue
				this.map().widget_cut( widget.link() )
			}
		}

		deserialize_map( dto: $shm_hitalama_transfer_map_dump ) {
			if( !Array.isArray( dto.nodes ) ) {
				throw new Error( 'Invalid hitalama_transfer dump: nodes should be an array' )
			}

			const map_link = this.map().link().str
			let states = $shm_hitalama_transfer_map_deserialize_states.get( dto )
			if( !states ) {
				states = new Map
				$shm_hitalama_transfer_map_deserialize_states.set( dto, states )
			}
			let state = states.get( map_link )
			if( state?.done ) return
			if( !state ) {
				state = { done: false, pawns: new Map,
					nodes: new Map( dto.nodes.map( node => [ node.link, node ] ) ), blobs: new Map }
				states.set( map_link, state )
			}
			this.pawn_by_old_link = state.pawns
			this.node_by_old_link = state.nodes
			this.blobs_by_uri = state.blobs
			this.normalize_render_data( dto )
			const root = this.node_by_old_link.get( dto.root_link )
			if( !root ) throw new Error( `Transfer root node not found: ${ dto.root_link }` )

			try {
				this.set_mapping( dto.root_link, this.entity() )
				this.instantiate_children( this.entity(), root )
				this.instantiate_table2_files( dto )

				this.deserialize_node_data( this.entity(), root )
				for( const node of dto.nodes ) {
					const pawn = this.pawn_by_old_link.get( node.link )
					if( !pawn || node.link === dto.root_link ) continue
					this.deserialize_node_data( pawn, node )
				}

				this.deserialize_node_links( this.entity(), root )
				for( const node of dto.nodes ) {
					const pawn = this.pawn_by_old_link.get( node.link )
					if( !pawn || node.link === dto.root_link ) continue
					this.deserialize_node_links( pawn, node )
				}
				this.restore_table2_values( dto )

				this.rebuild_widget_index()
				state.done = true
			} catch( error ) {
				if( !$mol_promise_like( error ) ) states.delete( map_link )
				throw error
			}
		}

		deserialize_widgets( dto: $shm_hitalama_transfer_map_widgets_dump ) {
			const map_link = this.map().link().str
			let states = $shm_hitalama_transfer_map_deserialize_states.get( dto )
			if( !states ) {
				states = new Map
				$shm_hitalama_transfer_map_deserialize_states.set( dto, states )
			}
			let state = states.get( map_link )
			if( state?.done ) return
			if( !state ) {
				state = {
					done: false,
					pawns: new Map,
					nodes: new Map( dto.nodes.map( node => [ node.link, node ] ) ),
					blobs: new Map,
				}
				states.set( map_link, state )
			}
			this.pawn_by_old_link = state.pawns
			this.node_by_old_link = state.nodes
			this.blobs_by_uri = state.blobs
			this.normalize_render_data( dto )

			try {
				for( const link of dto.root_links ) {

					const node = this.node_by_old_link.get( link )
					if( !node ) continue

					if( !this.should_deserialize_node({
						owner: this.map(),
						owner_link: this.map().link().toString(),
						field: 'Widgets',
						kind: 'links',
						node,
					}) ) continue

					let widget = this.pawn_by_old_link.get( link )?.cast( $shm_hitalama_map_widget )
					if( !widget ) {
						widget = this.instantiate_widget_node( node, this.map().land() )
						if( !widget ) continue
						this.set_mapping( link, widget )
					}

					widget.Map(null)?.remote( this.map() )
					this.instantiate_children( widget, node )
				}
				this.instantiate_table2_files( dto )

				for( const node of dto.nodes ) {
					const pawn = this.pawn_by_old_link.get( node.link )
					if( !pawn ) continue
					this.deserialize_node_data( pawn, node )
				}

				for( const node of dto.nodes ) {
					const pawn = this.pawn_by_old_link.get( node.link )
					if( !pawn ) continue
					this.deserialize_node_links( pawn, node )
				}
				this.restore_table2_values( dto )

				this.relink_widget_groups( dto )
				// All data and links are ready before the first list publication.
				this.publish_widgets()
				this.restore_removed_widgets( dto )
				this.rebuild_widget_index()
				state.done = true
			} catch( error ) {
				if( !$mol_promise_like( error ) ) states.delete( map_link )
				throw error
			}
		}

	}

	export class $shm_hitalama_transfer_map_restorer extends $shm_hitalama_transfer_map {
		deserialize_widgets( dto: $shm_hitalama_transfer_map_widgets_dump ) {
			this.normalize_render_data( dto )
			this.pawn_by_old_link.clear()
			this.node_by_old_link = new Map( dto.nodes.map( node => [ node.link, node ] ) )

			for( const link of dto.root_links ) {
				if( this.pawn_by_old_link.has( link ) ) continue

				const node = this.node_by_old_link.get( link )
				if( !node ) continue

				if( !this.should_deserialize_node({
					owner: this.map(),
					owner_link: this.map().link().toString(),
					field: 'Widgets',
					kind: 'links',
					node,
				}) ) continue

				const widget = this.map().Widgets_list()?.remote_list().find( ( item: $shm_hitalama_map_widget ) => item.link().toString() === link )
					?? $giper_baza_glob.Pawn( new $giper_baza_link( link ), $shm_hitalama_map_widget )

				if( !widget ) continue

				this.set_mapping( link, widget )
				this.instantiate_children( widget, node )
			}

			for( const node of dto.nodes ) {
				const pawn = this.pawn_by_old_link.get( node.link )
				if( !pawn ) continue
				this.deserialize_node_data( pawn, node )
			}

			for( const node of dto.nodes ) {
				const pawn = this.pawn_by_old_link.get( node.link )
				if( !pawn ) continue
				this.deserialize_node_links( pawn, node )
			}

			this.relink_widget_groups( dto )
			this.restore_removed_widgets( dto )
			this.rebuild_widget_index()
		}

		protected set_mapping( old_link: string, pawn: any ) {
			this.pawn_by_old_link.set( old_link, pawn )
		}

	}

}
